import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState, type FormEvent } from 'react'
import {
  CLICK_RACE_DURATIONS,
  COUNT_DURATIONS,
  GAME_MODES,
  MODE_META,
  STAKE_TIERS,
  type GameMode,
  type MatchConfig,
  type Purse,
  type Stake,
} from '@clicklinx/protocol'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useGame } from '../lib/socket'
import { Icon, Spinner } from '../components/Icon'
import { Logo } from '../components/Logo'
import { LegalLinks } from './LegalScreen'
import { ShapeGlyph } from '../components/Shape'
import { Button, CoinChip, Panel, Segmented, TextField, cx } from '../components/ui'
import { formatDuration } from '../lib/format'
import { formatCents, getActivity, getPaymentsConfig, type PaymentsConfig } from '../lib/payments'

const EASE = [0.22, 1, 0.36, 1] as const

export function HomeScreen({ onOpenWallet }: { onOpenWallet: () => void }) {
  const { user, logout, setBalance } = useAuth()
  const { createRoom, joinRoom, joinQueue, leaveQueue, startPractice, queued, connected } = useGame()

  const [purse, setPurse] = useState<Purse>('practice')
  const [mode, setMode] = useState<GameMode>('click_race')
  const [duration, setDuration] = useState<number>(30)
  const [stake, setStake] = useState<Stake>(100)
  const [maxPlayers, setMaxPlayers] = useState(2)
  const [payments, setPayments] = useState<PaymentsConfig | null>(null)
  const [activity, setActivity] = useState<{ online: number; queued: number } | null>(null)
  const [waitedLong, setWaitedLong] = useState(false)

  // Whether prize play is offered here at all. Fetched rather than assumed: it
  // depends on the player's location, and offering a button that can only fail
  // is worse than not offering it.
  useEffect(() => {
    let live = true
    void getPaymentsConfig()
      .then((config) => {
        if (live) setPayments(config)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  // Who else is around. Polled rather than pushed: it is a reassurance, not a
  // game mechanic, and a stale count by a few seconds costs nothing.
  useEffect(() => {
    let live = true
    const read = () => {
      void getActivity()
        .then((next) => {
          if (live) setActivity(next)
        })
        .catch(() => undefined)
    }
    read()
    const timer = window.setInterval(read, 15_000)
    return () => {
      live = false
      window.clearInterval(timer)
    }
    // Re-read as soon as the socket comes up: the first poll fires before the
    // connection is established, so it reports a lobby that does not yet
    // include this player.
  }, [connected])

  // Searching forever with nobody to find is the moment a new player decides
  // the game is dead. After a short wait the lobby says so and offers the two
  // things that always work: invite someone, or play the bot.
  useEffect(() => {
    if (!queued) {
      setWaitedLong(false)
      return
    }
    const timer = window.setTimeout(() => setWaitedLong(true), 20_000)
    return () => window.clearTimeout(timer)
  }, [queued])

  const [joinCode, setJoinCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<'create' | 'join' | 'queue' | 'bonus' | 'practice' | null>(null)

  const durations = mode === 'click_race' ? CLICK_RACE_DURATIONS : COUNT_DURATIONS

  const selectMode = (next: GameMode) => {
    setMode(next)
    // The two families use different scales, so carry over a sensible default
    // rather than leaving an invalid duration selected.
    setDuration(next === 'click_race' ? 30 : 20)
  }

  const config: MatchConfig = { mode, duration, stake, maxPlayers, purse }
  const isPrize = purse === 'prize'
  const purseBalance = isPrize ? (user?.prizeCoins ?? 0) : (user?.balance ?? 0)
  const canAfford = purseBalance >= stake
  const prizeOffered = payments?.enabled !== false && payments?.moneyAllowed !== false

  /**
   * The bot is always paid for in practice coins, so a prize-purse lobby still
   * needs a sensible practice ante rather than reusing a dollar figure.
   */
  const botStake: Stake = isPrize ? 100 : stake

  /** Stake tiers read as coins in practice and as money in prize. */
  const stakeLabel = (value: number) => (isPrize ? formatCents(value) : value.toLocaleString())

  const run = async (
    kind: 'create' | 'join' | 'queue' | 'practice',
    action: () => Promise<unknown>,
  ) => {
    setError(null)
    setBusy(kind)
    try {
      await action()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Something went wrong.')
    } finally {
      setBusy(null)
    }
  }

  const claimBonus = async () => {
    setError(null)
    setBusy('bonus')
    try {
      const result = await api<{ balance: number }>('/me/daily-bonus', { method: 'POST', auth: true })
      setBalance('practice', result.balance)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not claim right now.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="min-h-dvh pad-safe pad-safe-top">
      {/*
        The right-hand group has to survive a 360px screen, which is the most
        common phone width there is. It did not: the logo, the balance, Wallet
        and Sign out came to 384px against 328px of room, and `overflow-x` on
        the body quietly clipped the overspill - so on a phone the Sign out
        button was rendered entirely off the right edge and could not be
        reached at all. Below `sm` the wordmark and the Wallet label give way,
        and `shrink-0` keeps a longer balance from pushing anything out again.
      */}
      <header className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 py-4 sm:gap-4">
        <Logo size="sm" wordmark="sm" />
        <div className="flex shrink-0 items-center gap-2">
          {/* Both balances, always. Which one an ante comes out of is the whole
              point of the purse switch, so hiding the inactive one would make
              the switch feel like it did nothing. */}
          <CoinChip amount={user?.balance ?? 0} tone={isPrize ? 'muted' : 'gold'} />
          {prizeOffered && (
            <span
              className={cx(
                'hidden items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-sm font-medium tnum sm:inline-flex',
                isPrize
                  ? 'border-lime/30 bg-lime/10 text-lime'
                  : 'border-transparent bg-transparent text-fg-dim',
              )}
              title="Prize coins"
            >
              <Icon name="trophy" size={15} />
              {formatCents(user?.prizeCoins ?? 0)}
            </span>
          )}
          <button
            type="button"
            onClick={onOpenWallet}
            aria-label="Wallet and cashouts"
            className="flex h-11 w-11 items-center justify-center gap-1.5 rounded-xl border border-ink-700 text-sm text-fg-muted transition-colors hover:border-ink-600 hover:text-fg sm:w-auto sm:px-3"
          >
            <Icon name="coin" size={16} />
            <span className="hidden sm:inline">Wallet</span>
          </button>
          <button
            type="button"
            onClick={logout}
            aria-label="Sign out"
            className="flex h-11 w-11 items-center justify-center rounded-xl text-fg-dim transition-colors hover:bg-ink-850 hover:text-fg"
          >
            <Icon name="logout" size={18} />
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl pb-16">
        <div className="mb-8 mt-2">
          <h1 className="display-title text-2xl text-fg sm:text-3xl">
            Hello, {user?.displayName.split(' ')[0]}
          </h1>
          <p className="mt-2 text-sm text-fg-muted">
            Pick a mode, set the ante, and take someone&apos;s coins.
          </p>
        </div>

        {!connected && (
          <div className="mb-6 flex items-center gap-2.5 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            <Icon name="wifiOff" size={16} />
            Reconnecting to the game server…
          </div>
        )}

        {/* The purse decides the currency, the opponent, and whether anything
            can be withdrawn. It governs every control below it, so it sits
            above them rather than among the match settings. */}
        {prizeOffered && (
          <section aria-labelledby="purse-heading" className="mb-8">
            <p id="purse-heading" className="eyebrow mb-3">
              Playing for
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <PurseCard
                selected={!isPrize}
                onSelect={() => setPurse('practice')}
                icon="target"
                title="Practice coins"
                detail="Play anyone, or the bot. Free to top up, never withdrawable."
                balance={(user?.balance ?? 0).toLocaleString()}
              />
              <PurseCard
                selected={isPrize}
                onSelect={() => setPurse('prize')}
                icon="trophy"
                title="Prize coins"
                detail="Real people only. Converts back to cash any time."
                balance={formatCents(user?.prizeCoins ?? 0)}
              />
            </div>
          </section>
        )}

        {/* Mode selection */}
        <section aria-labelledby="mode-heading" className="mb-8">
          <p id="mode-heading" className="eyebrow mb-3">
            Game mode
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {GAME_MODES.map((value) => (
              <ModeCard
                key={value}
                mode={value}
                selected={mode === value}
                onSelect={() => selectMode(value)}
              />
            ))}
          </div>
        </section>

        {/* Settings */}
        <Panel className="mb-6 p-5 sm:p-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <Segmented
              label={mode === 'click_race' ? 'Race length' : 'Round one display time'}
              value={duration}
              onChange={setDuration}
              options={durations.map((value) => ({
                value: value as number,
                label: formatDuration(value),
              }))}
            />

            <Segmented
              label="Players"
              value={maxPlayers}
              onChange={setMaxPlayers}
              options={[2, 3, 4].map((value) => ({
                value,
                label: String(value),
                sublabel: value === 2 ? 'Duel' : 'Group',
              }))}
            />
          </div>

          <div className="mt-6">
            <Segmented
              label="Ante per player"
              value={stake}
              onChange={(next) => setStake(next as Stake)}
              columns={5}
              options={STAKE_TIERS.map((value) => ({
                value: value as number,
                label: stakeLabel(value),
                disabled: purseBalance < value,
              }))}
            />
            {/* The pot is the number the player is actually deciding on, so it
                is not relegated to 12px dim grey like a footnote. */}
            <p className="mt-3.5 flex items-center gap-2 text-sm text-fg-muted">
              <Icon name={isPrize ? 'trophy' : 'coin'} size={15} className={isPrize ? 'text-lime' : 'text-gold'} />
              Pot at {maxPlayers} players
              <span
                className={cx(
                  'font-mono text-base font-semibold tnum',
                  isPrize ? 'text-lime' : 'text-gold',
                )}
              >
                {stakeLabel(stake * maxPlayers)}
              </span>
            </p>
          </div>

          {mode !== 'click_race' && (
            <p className="mt-5 rounded-xl border border-ink-700 bg-ink-950/50 px-4 py-3 text-xs leading-relaxed text-fg-dim">
              Each round the display window tightens and the count grows. Get it right when
              your opponent gets it wrong and they are out — match everyone and the round
              simply runs again, harder.
            </p>
          )}
        </Panel>

        {activity !== null && connected && (
          <p className="mb-4 flex items-center gap-2 text-xs text-fg-dim">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-lime" />
            {/* Floored at one. A connected player being told nobody is online
                is plainly wrong, and it discredits the number precisely when it
                is meant to be reassuring. */}
            {Math.max(activity.online, 1) === 1
              ? 'You are the only one here right now'
              : `${Math.max(activity.online, 1)} players online`}
            {activity.queued > 0 && `, ${activity.queued} searching`}
          </p>
        )}

        {error && (
          <p role="alert" className="mb-4 flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            <Icon name="close" size={16} className="mt-0.5 shrink-0" />
            {error}
          </p>
        )}

        {/* Actions.
            Inviting someone is the primary path in both purses, and it is
            first because it is the one that always works: it needs one person
            you already know rather than a stranger who happens to be searching
            the same bucket at the same second. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Button
            size="lg"
            fullWidth
            disabled={!canAfford || !connected || queued}
            loading={busy === 'create'}
            onClick={() => run('create', () => createRoom(config, false))}
            icon={<Icon name="plus" size={18} />}
          >
            Invite a friend
          </Button>

          {queued ? (
            <Button size="lg" fullWidth variant="danger" onClick={leaveQueue}>
              <Spinner size={17} />
              Searching — cancel
            </Button>
          ) : (
            <Button
              size="lg"
              fullWidth
              variant="outline"
              disabled={!canAfford || !connected}
              loading={busy === 'queue'}
              onClick={() => run('queue', () => joinQueue({ ...config, maxPlayers: 2 }))}
              icon={<Icon name="bolt" size={18} />}
            >
              Quick match
              {activity !== null && activity.queued > 0 && (
                <span className="ml-1 font-mono text-xs tnum text-lime">
                  {activity.queued} waiting
                </span>
              )}
            </Button>
          )}
        </div>

        {/* The escape hatch from an empty queue. */}
        {queued && waitedLong && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gold/25 bg-gold/8 px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-gold">
                <Icon name="clock" size={20} />
              </span>
              <div>
                <p className="text-sm font-semibold text-fg">Nobody is searching this bucket</p>
                <p className="mt-1 text-xs leading-relaxed text-fg-dim">
                  Send someone a link instead — it takes one person you already know.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                leaveQueue()
                void run('create', () => createRoom(config, false))
              }}
            >
              Invite a friend
              <Icon name="arrowRight" size={16} />
            </Button>
          </div>
        )}

        {/* The bot. Always available, always practice coins - it is how a mode
            gets learned, and it is what fills the gap when nobody is around. */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink-700 bg-ink-900/60 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-fg-dim">
              <Icon name="target" size={20} />
            </span>
            <div>
              <p className="text-sm font-semibold text-fg">
                {isPrize ? `New to ${MODE_META[mode].name}?` : 'No one around?'}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-fg-dim">
                Play the bot for practice coins. It covers its own side of the pot, so
                beating it is worth something.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!connected || queued || (user?.balance ?? 0) < botStake}
            loading={busy === 'practice'}
            onClick={() => run('practice', () => startPractice(mode, duration, botStake))}
          >
            Play the bot for {botStake.toLocaleString()}
            <Icon name="arrowRight" size={16} />
          </Button>
        </div>

        {!isPrize && prizeOffered && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink-700 bg-ink-900/60 px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-fg-dim">
                <Icon name="lock" size={20} />
              </span>
              <div>
                <p className="text-sm font-semibold text-fg">Practice coins stay practice coins</p>
                <p className="mt-1 text-xs leading-relaxed text-fg-dim">
                  They never convert to money, in either direction. Switch to prize coins when
                  you want a pot that is worth cash.
                </p>
              </div>
            </div>
          </div>
        )}

        {!canAfford && (
          <div
            className={cx(
              'mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3.5',
              isPrize ? 'border-lime/25 bg-lime/8' : 'border-gold/25 bg-gold/8',
            )}
          >
            <p className={cx('text-sm', isPrize ? 'text-lime' : 'text-gold')}>
              {isPrize
                ? `You need ${stakeLabel(stake)} in prize coins for this ante.`
                : `You need ${stake.toLocaleString()} practice coins for this ante.`}
            </p>
            {isPrize ? (
              <Button size="sm" variant="outline" onClick={onOpenWallet}>
                Convert from balance
              </Button>
            ) : (
              <Button size="sm" variant="outline" loading={busy === 'bonus'} onClick={claimBonus}>
                Claim daily coins
              </Button>
            )}
          </div>
        )}

        {/* Join by code */}
        <Panel flush className="mt-8 p-5 sm:p-6">
          <p className="eyebrow mb-3">Have a room code?</p>
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              void run('join', () => joinRoom(joinCode))
            }}
          >
            <div className="flex-1">
              <TextField
                label="Room code"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={6}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                className="font-mono text-lg tracking-[0.3em]"
              />
            </div>
            <Button
              type="submit"
              size="lg"
              variant="outline"
              disabled={joinCode.trim().length !== 6 || !connected}
              loading={busy === 'join'}
            >
              Join
              <Icon name="arrowRight" size={18} />
            </Button>
          </form>
        </Panel>

        <div className="mt-10 flex flex-col items-center gap-3">
          <p className="text-center text-xs text-fg-dim">
            Practice coins are free and hold no cash value.
          </p>
          <LegalLinks className="justify-center" />
        </div>
      </main>
    </div>
  )
}

/**
 * One of the two purses.
 *
 * Deliberately a card rather than a segmented toggle: this choice changes the
 * currency, the opponent, and whether anything can be withdrawn, and none of
 * that fits in a tab label.
 */
function PurseCard({
  selected,
  onSelect,
  icon,
  title,
  detail,
  balance,
}: {
  selected: boolean
  onSelect: () => void
  icon: 'target' | 'trophy'
  title: string
  detail: string
  balance: string
}) {
  const accent = icon === 'trophy' ? 'lime' : 'gold'
  return (
    <motion.button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.2, ease: EASE }}
      className={cx(
        'group relative flex items-start gap-3.5 rounded-2xl border p-4 text-left transition-colors',
        selected
          ? accent === 'lime'
            ? 'border-lime/45 bg-lime/8'
            : 'border-gold/45 bg-gold/8'
          : 'border-ink-700 bg-ink-900/60 hover:border-ink-600',
      )}
    >
      <span
        className={cx(
          'mt-0.5 transition-colors',
          selected ? (accent === 'lime' ? 'text-lime' : 'text-gold') : 'text-fg-dim',
        )}
      >
        <Icon name={icon} size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className={cx('text-sm font-semibold', selected ? 'text-fg' : 'text-fg-muted')}>
            {title}
          </span>
          <span
            className={cx(
              'shrink-0 font-mono text-sm font-medium tnum',
              selected ? (accent === 'lime' ? 'text-lime' : 'text-gold') : 'text-fg-dim',
            )}
          >
            {balance}
          </span>
        </span>
        <span className="mt-1 block text-xs leading-relaxed text-fg-dim">{detail}</span>
      </span>
    </motion.button>
  )
}

function ModeCard({
  mode,
  selected,
  onSelect,
}: {
  mode: GameMode
  selected: boolean
  onSelect: () => void
}) {
  const meta = MODE_META[mode]

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      whileTap={{ scale: 0.985 }}
      transition={{ type: 'spring', stiffness: 620, damping: 32 }}
      className={cx(
        'no-tap-delay relative overflow-hidden rounded-2xl border p-5 text-left transition-colors duration-200',
        selected
          ? 'border-lime/50 bg-lime-wash'
          : 'border-ink-700 bg-ink-900 hover:border-ink-600',
      )}
    >
      <div className={cx('mb-4 h-16', selected ? 'text-lime' : 'text-fg-dim')}>
        <ModePreview mode={mode} active={selected} />
      </div>

      <p className={cx('display-title text-[0.8125rem]', selected ? 'text-lime' : 'text-fg')}>
        {meta.name}
      </p>
      <p className="mt-2 text-xs leading-relaxed text-fg-muted">{meta.tagline}</p>

      <AnimatePresence>
        {selected && (
          <motion.span
            className="absolute right-4 top-4 text-lime"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            <Icon name="check" size={17} />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  )
}

/** A miniature of what the mode actually looks like, not a generic glyph. */
function ModePreview({ mode, active }: { mode: GameMode; active: boolean }) {
  if (mode === 'click_race') {
    return (
      <div className="relative flex h-full items-center">
        <div className="relative h-14 w-14">
          {[1, 0.68, 0.36].map((scale, index) => (
            <motion.span
              key={scale}
              className="absolute left-1/2 top-1/2 rounded-full border-2 border-current"
              style={{
                width: `${scale * 100}%`,
                height: `${scale * 100}%`,
                transform: 'translate(-50%, -50%)',
              }}
              animate={active ? { opacity: [0.3, 1, 0.3] } : { opacity: 0.45 }}
              transition={{ duration: 1.6, repeat: Infinity, delay: index * 0.18 }}
            />
          ))}
        </div>
      </div>
    )
  }

  if (mode === 'count_sequential') {
    return (
      <div className="flex h-full items-center gap-2.5">
        {(['triangle', 'triangle', 'triangle'] as const).map((shape, index) => (
          <motion.span
            key={index}
            animate={active ? { opacity: [0.15, 1, 0.15] } : { opacity: 0.4 }}
            transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.4 }}
          >
            <ShapeGlyph shape={shape} size={28} />
          </motion.span>
        ))}
      </div>
    )
  }

  return (
    <div className="grid h-full grid-cols-4 items-center gap-1.5">
      {(['star', 'circle', 'star', 'square', 'hexagon', 'star', 'diamond', 'circle'] as const).map(
        (shape, index) => (
          <span key={index} style={{ opacity: shape === 'star' ? 1 : 0.32 }}>
            <ShapeGlyph shape={shape} size={17} />
          </span>
        ),
      )}
    </div>
  )
}
